import {
    useBodyScrollLock,
    useScrollFocusedIntoView,
    useVisualViewportProperties,
} from "@xapp/chat-widget-core";
import React, { FC, PropsWithChildren } from "react";
import styles from "./ModalDialog.module.scss";

export interface ModalDialogProps {
    /**
     * Removes the backdrop overlay
     *
     * Setting this to true will then disable the click backdrop to close functionality
     */
    readonly disableBackdrop?: boolean;
    /**
     * Callback when the user requests to close the modal by clicking the backdrop.
     */
    onClose(): void;
    /**
     * Rendering inside a container the host page provided and sized (see `isEmbedded`).
     *
     * The customer's div is then the frame, so everything this component does for the
     * floating case -- a fixed full-viewport overlay, a backdrop over the host page, and
     * locking the host page's scroll -- is dropped.
     */
    readonly embedded?: boolean;
}

export const ModalDialog: FC<PropsWithChildren<ModalDialogProps>> = (props) => {
    const { embedded } = props;

    // This used to add a class that set `body { overflow: hidden }`, which iOS Safari
    // ignores — the host page scrolled on underneath the open modal. Embedded there is no
    // overlay to scroll underneath: the form sits in the page flow, and pinning the body
    // around it would freeze the customer's whole page.
    useBodyScrollLock(!embedded);

    // Keeps the step's action row clear of the soft keyboard — see the calc() on
    // .xapp_search_modal's mobile padding. Reference counted, so the chat widget's own
    // subscription survives this modal closing while its keyboard is still up. Embedded,
    // nothing consumes --xapp-keyboard-inset, so there is nothing to publish it for.
    useVisualViewportProperties(!embedded);

    // Browsers do this themselves, but not reliably inside a position: fixed overlay -
    // tapping the last field on a step could leave it behind the keyboard. Kept on when
    // embedded: the customer's container is commonly a scroll box with the same problem.
    useScrollFocusedIntoView();

    if (embedded) {
        return <div className={styles.xapp_search_modal_embedded}>{props.children}</div>;
    }

    return (
        <div className={styles.xapp_search_modal}>
            {!props.disableBackdrop && (
                <div
                    data-testid="xapp-modal-backdrop"
                    className={styles["xapp_search_modal__backdrop"]}
                    onClick={props.onClose}
                />
            )}
            <div className={styles["xapp_search_modal__body"]}>{props.children}</div>
        </div>
    );
};
